import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled, EyeOutlined } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import ShoppingCentersAPI from 'libs/api/admin/shoppingCenters';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import UIHelper from '../../../../helpers/UIHelper';
import Helpers from '../../../../helpers';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';

const api = new ShoppingCentersAPI();

const useShoppingCenter = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const pages = useRecoilValue(getPage);

  const {
    data,
    singleItem: singleShop,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'shopping_centers',
    singleResourceKey: 'shopping_center',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const handleDownloadBtbLoading = async (id) => {
    try {
      setXlsxLoading(true);
      const response = await api.getB2BExcel(id);
      Helpers.downloadFile(response, 'B2B.xlsx');
    } catch (e) {
      console.log(e);
      UIHelper.serverError();
    } finally {
      setXlsxLoading(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Catena'),
        dataIndex: 'title',
        width: 100,
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteFilled />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, xlsxLoading, pages?.ability?.can_read]);

  const handleCreateOrUpdateShop = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        // Extract extrastock_map separately to handle it properly
        const { extrastock_map, ...restValues } = values;

        // Check if we need to send file via FormData
        const hasNewFile = extrastock_map && 
                          Array.isArray(extrastock_map) && 
                          extrastock_map.length > 0 && 
                          extrastock_map[0].originFileObj;

        if (hasNewFile) {
          // Use FormData for file upload
          const formData = new FormData();
          
          // Add all shopping_center fields
          Object.keys(restValues).forEach(key => {
            formData.append(`shopping_center[${key}]`, restValues[key] || '');
          });
          
          formData.append('shopping_center[working_hours_it]', values.working_hours_it || '');
          formData.append('shopping_center[working_hours_en]', values.working_hours_en || '');
          formData.append('shopping_center[working_hours_fr]', values.working_hours_fr || '');
          
          // Add file
          formData.append('shopping_center[extrastock_map]', extrastock_map[0].originFileObj);

          if (!id) {
            await api.create({ data: formData });
            navigate('/admin/shopping_centers');
          } else {
            await api.put(id, { data: formData });
            navigate(`/admin/shopping_centers/${id}?success=true`);
          }
        } else {
          // No file - use regular JSON
          const data = {
            shopping_center: {
              ...restValues,
              working_hours_it: values.working_hours_it || '',
              working_hours_en: values.working_hours_en || '',
              working_hours_fr: values.working_hours_fr || '',
            },
          };

          if (!id) {
            await api.create({ data });
            navigate('/admin/shopping_centers');
          } else {
            await api.put(id, { data });
            navigate(`/admin/shopping_centers/${id}?success=true`);
          }
        }
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );

  useEffect(() => {
    if (id && !isMainPage && singleShop) {
      try {
        let fieldsValues = [];
        for (let key in singleShop) {
          if (key !== 'id' && key !== 'extrastock_map') {
            fieldsValues.push({
              name: key,
              value: singleShop[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, isMainPage, singleShop, form]);

  return {
    columns,
    data,
    singleShop,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    handleCreateOrUpdateShop,
    handleDeleteCategory,
  };
};

export default useShoppingCenter;
